// Weather model — pure decisions, no DOM and no I/O.
//
// Everything `ctx.weather` decides *about a response* lives here so `node --test`
// can assert it: the WMO-code table, the coercions, and — the part that only
// exists because a wallpaper asked for it — the translation from a forecast into
// the handful of 0..1 numbers a *visual* actually wants.
//
// `.mjs` (not `.ts`) on purpose: `node --test` cannot import a `.ts` source, and
// `tsc` emits into `dist/`, so a TypeScript sibling would leave the test with
// nothing to import. Types are mirrored in ./weather-model.d.mts.
//
// KEEP IN SYNC with ./weather-model.d.mts and the SDK's WeatherSnapshot.
//
// THE PARSING RULE, same as everywhere else that touches the wire: a field that
// is missing, non-numeric or the wrong type means "no reading" — never zero.
// A shader fed `undefined` renders black, and a shader fed a silent 0 renders a
// clear sky during a thunderstorm. Both are worse than declining the reading.

/** Location the OS asks about until someone sets one. Matches widget.weather. */
export const DEFAULT_LOCATION = "Austin";

/** How often a subscribed OS refreshes. The upstream TTL is 10min; match it. */
export const REFRESH_MS = 10 * 60_000;

/** A snapshot younger than this is reused instead of refetched. */
export const FRESH_MS = 5 * 60_000;

/** Failure backoff for the poll loop, mirroring app.trading's. */
export const BACKOFF_MS = [15_000, 30_000, 60_000];

/**
 * How long until the next poll, given consecutive failed refreshes.
 *
 * A failing poll speeds UP rather than gives up. The failure this exists for is
 * transient — a laptop waking to a resolver that died while it slept — and on a
 * flat ten-minute interval the tile sat there reading "unavailable" for ten
 * minutes after the network was already back, unless the user tapped it.
 * Recovering to REFRESH_MS on the FIRST success is what keeps a healthy desktop
 * at one request per proxy TTL: the fast retries only exist while it is broken.
 *
 * Pure, and here rather than in weather-host.ts, for the reason net-http.mjs
 * gives about api.ts: the host is timers and browser globals and cannot be
 * imported under node --test, so the one decision worth a regression test is
 * pulled out where a test can reach it.
 *
 * @param {number} failures consecutive failures; 0 means the last poll worked
 * @returns {number}
 */
export function nextPollDelayMs(failures) {
  if (!(failures > 0)) return REFRESH_MS;
  return BACKOFF_MS[Math.min(Math.floor(failures) - 1, BACKOFF_MS.length - 1)];
}

/** Shorter than the proxy's 8s ceiling: geocode + forecast chain. */
export const HTTP_TIMEOUT_MS = 6000;

/** Beyond this a cached reading is no longer worth showing at all. */
export const MAX_CACHE_AGE_MS = 12 * 60 * 60_000;

/** A failure we diagnosed ourselves (bad shape, unknown city), not one ctx.http threw. */
export class WeatherDataError extends Error {
  constructor(message) {
    super(message);
    this.name = "WeatherDataError";
  }
}

/* ── WMO 4677 → what a renderer needs ──────────────────────────────────────
 * `kind` is the coarse bucket every consumer switches on. `precip`, `fog` and
 * `storm` are 0..1 *intensities*, because a visual wants "how hard is it
 * raining", not "is code 63 in this set". A code we don't know falls back to a
 * neutral entry rather than leaking the bare number.
 * ------------------------------------------------------------------------- */

const CODES = {
  0: { kind: "clear", label: "Clear", precip: 0, fog: 0, storm: 0 },
  1: { kind: "clear", label: "Mainly clear", precip: 0, fog: 0, storm: 0 },
  2: { kind: "partly-cloudy", label: "Partly cloudy", precip: 0, fog: 0, storm: 0 },
  3: { kind: "cloudy", label: "Overcast", precip: 0, fog: 0.15, storm: 0 },
  45: { kind: "fog", label: "Fog", precip: 0, fog: 1, storm: 0 },
  48: { kind: "fog", label: "Rime fog", precip: 0, fog: 1, storm: 0 },
  51: { kind: "drizzle", label: "Light drizzle", precip: 0.2, fog: 0.25, storm: 0 },
  53: { kind: "drizzle", label: "Drizzle", precip: 0.32, fog: 0.3, storm: 0 },
  55: { kind: "drizzle", label: "Heavy drizzle", precip: 0.45, fog: 0.35, storm: 0 },
  56: { kind: "drizzle", label: "Freezing drizzle", precip: 0.3, fog: 0.35, storm: 0 },
  57: { kind: "drizzle", label: "Freezing drizzle", precip: 0.42, fog: 0.4, storm: 0 },
  61: { kind: "rain", label: "Light rain", precip: 0.4, fog: 0.2, storm: 0 },
  63: { kind: "rain", label: "Rain", precip: 0.65, fog: 0.3, storm: 0 },
  65: { kind: "rain", label: "Heavy rain", precip: 0.92, fog: 0.4, storm: 0 },
  66: { kind: "rain", label: "Freezing rain", precip: 0.6, fog: 0.3, storm: 0 },
  67: { kind: "rain", label: "Freezing rain", precip: 0.85, fog: 0.4, storm: 0 },
  71: { kind: "snow", label: "Light snow", precip: 0.35, fog: 0.3, storm: 0 },
  73: { kind: "snow", label: "Snow", precip: 0.62, fog: 0.45, storm: 0 },
  75: { kind: "snow", label: "Heavy snow", precip: 0.95, fog: 0.6, storm: 0 },
  77: { kind: "snow", label: "Snow grains", precip: 0.3, fog: 0.35, storm: 0 },
  80: { kind: "rain", label: "Light showers", precip: 0.45, fog: 0.2, storm: 0.1 },
  81: { kind: "rain", label: "Showers", precip: 0.7, fog: 0.28, storm: 0.2 },
  82: { kind: "rain", label: "Violent showers", precip: 1, fog: 0.35, storm: 0.35 },
  85: { kind: "snow", label: "Snow showers", precip: 0.55, fog: 0.4, storm: 0 },
  86: { kind: "snow", label: "Snow showers", precip: 0.85, fog: 0.55, storm: 0 },
  95: { kind: "thunder", label: "Thunderstorm", precip: 0.8, fog: 0.3, storm: 0.85 },
  96: { kind: "thunder", label: "Thunderstorm, hail", precip: 0.9, fog: 0.35, storm: 1 },
  99: { kind: "thunder", label: "Thunderstorm, hail", precip: 1, fog: 0.4, storm: 1 },
};

const UNKNOWN = { kind: "clear", label: "—", precip: 0, fog: 0, storm: 0 };

/**
 * The visual bucket for a WMO code. Never throws, never returns undefined.
 * @param {unknown} code
 */
export function describe(code) {
  const hit = typeof code === "number" ? CODES[code] : undefined;
  return hit || UNKNOWN;
}

/* ── small coercions ──────────────────────────────────────────────────────── */

/**
 * A finite number, or null. `null`, `"12"`, `undefined` and `NaN` all mean
 * "no reading" — the caller decides whether that is fatal.
 * @param {unknown} v
 * @returns {number | null}
 */
export function num(v) {
  return typeof v === "number" && Number.isFinite(v) ? v : null;
}

/** Clamp into 0..1. Anything unusable becomes `fallback`, never NaN. */
export function unit(v, fallback = 0) {
  const n = num(v);
  if (n === null) return fallback;
  return n < 0 ? 0 : n > 1 ? 1 : n;
}

/* ── request URLs ─────────────────────────────────────────────────────────── */

/** @param {string} location */
export function geocodeUrl(location) {
  return (
    "https://geocoding-api.open-meteo.com/v1/search?name=" +
    encodeURIComponent(location) +
    "&count=1"
  );
}

/**
 * Current conditions only — no daily block.
 *
 * This asks for MORE than widget.weather does (`cloud_cover`, `precipitation`,
 * `is_day`) because a code alone cannot drive a sky: "partly cloudy" is a range,
 * and a wallpaper needs the number inside it. Always metric; a snapshot carries
 * SI and each consumer formats for its own audience.
 *
 * @param {number} lat
 * @param {number} lon
 */
export function currentUrl(lat, lon) {
  return (
    "https://api.open-meteo.com/v1/forecast?latitude=" +
    encodeURIComponent(lat.toFixed(3)) +
    "&longitude=" +
    encodeURIComponent(lon.toFixed(3)) +
    "&current=temperature_2m,weather_code,cloud_cover,precipitation,wind_speed_10m,is_day" +
    "&wind_speed_unit=kmh" +
    "&timezone=auto"
  );
}

/* ── response parsing ─────────────────────────────────────────────────────── */

/**
 * Coordinates out of a geocoding response.
 *
 * An empty `results` is the likeliest failure in normal use — a typo'd city —
 * so it gets a message naming the input rather than a generic "unavailable".
 *
 * @param {unknown} data
 * @param {string} location
 * @returns {{ lat: number, lon: number, place: string }}
 */
export function parseGeocode(data, location) {
  const d = data && typeof data === "object" ? /** @type {any} */ (data) : {};
  const results = Array.isArray(d.results) ? d.results : [];
  const hit = results[0];
  const lat = num(hit?.latitude);
  const lon = num(hit?.longitude);
  if (!hit || lat === null || lon === null) {
    throw new WeatherDataError(`Can't find "${location}"`);
  }
  const name = typeof hit.name === "string" && hit.name ? hit.name : location;
  const region = typeof hit.admin1 === "string" && hit.admin1 ? hit.admin1 : "";
  const country = typeof hit.country_code === "string" ? hit.country_code : "";
  const place =
    region && region !== name ? `${name}, ${region}` : country ? `${name}, ${country}` : name;
  return { lat, lon, place };
}

/**
 * Current conditions out of a forecast response.
 *
 * The temperature is the one required field: without it there is no reading.
 * Everything else degrades — a missing `cloud_cover` is inferred from the code
 * rather than treated as a clear sky, which is the failure that would show up
 * as a sunny wallpaper during an overcast afternoon.
 *
 * @param {unknown} data
 */
export function parseCurrent(data) {
  const d = data && typeof data === "object" ? /** @type {any} */ (data) : {};
  const cur = d.current;
  const tempC = num(cur?.temperature_2m);
  if (tempC === null) throw new WeatherDataError("Bad weather data");

  const code = num(cur?.weather_code) ?? -1;
  const spec = describe(code);
  const reported = num(cur?.cloud_cover);

  return {
    tempC,
    code,
    // Percent on the wire. Absent → the code's own implied cover, so an
    // overcast reading never renders as a clear sky.
    cloud: reported === null ? impliedCloud(spec.kind) : unit(reported / 100),
    precipMm: Math.max(0, num(cur?.precipitation) ?? 0),
    windKph: Math.max(0, num(cur?.wind_speed_10m) ?? 0),
    // 1/0 on the wire. Absent → null, and the caller falls back to ctx.time,
    // which is the better source anyway when the two disagree.
    isDay: cur?.is_day === undefined ? null : num(cur.is_day) === 1,
  };
}

/** Cloud cover a `kind` implies, for responses that omit `cloud_cover`. */
function impliedCloud(kind) {
  if (kind === "clear") return 0.1;
  if (kind === "partly-cloudy") return 0.45;
  return 0.9;
}

/* ── snapshot ─────────────────────────────────────────────────────────────── */

/**
 * Fold a parsed reading into the shape consumers actually render.
 *
 * `precip` blends the code's nominal intensity with the measured mm/h: the code
 * says what *kind* of rain it is, the measurement says how hard it is falling
 * right now, and a wallpaper that used only the code looked identical in a
 * drizzle and a downpour. Capped at 1 — 4 mm/h is already a wall of water on
 * screen, and the scale above that is not visually meaningful.
 *
 * @param {string} place
 * @param {ReturnType<typeof parseCurrent>} cur
 * @param {number} at epoch ms
 */
export function snapshotFrom(place, cur, at) {
  const spec = describe(cur.code);
  const measured = Math.min(1, cur.precipMm / 4);
  const precip = spec.precip === 0 ? 0 : Math.min(1, Math.max(spec.precip * 0.65, measured));
  return {
    place,
    code: cur.code,
    kind: spec.kind,
    label: spec.label,
    tempC: cur.tempC,
    windKph: cur.windKph,
    cloud: cur.cloud,
    precip,
    fog: spec.fog,
    storm: spec.storm,
    isDay: cur.isDay,
    at,
    stale: false,
  };
}

/**
 * Validate a snapshot read back out of localStorage.
 *
 * Storage survives upgrades and can be hand-edited, so this is untrusted input
 * like any other: anything without a place, a temperature and a timestamp is
 * treated as no cache at all, and every 0..1 field is re-clamped rather than
 * trusted — an out-of-range value reaches a shader uniform unchecked.
 *
 * @param {unknown} raw
 * @param {number} [now]
 */
export function reviveSnapshot(raw, now = Date.now()) {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  const r = /** @type {any} */ (raw);
  const at = num(r.at);
  const tempC = num(r.tempC);
  if (at === null || tempC === null || typeof r.place !== "string" || !r.place) return null;
  // A reading from yesterday is not "stale weather", it is no information.
  if (now - at > MAX_CACHE_AGE_MS) return null;

  const code = num(r.code) ?? -1;
  const spec = describe(code);
  return {
    place: r.place,
    code,
    kind: spec.kind,
    label: spec.label,
    tempC,
    windKph: Math.max(0, num(r.windKph) ?? 0),
    cloud: unit(r.cloud, impliedCloud(spec.kind)),
    precip: unit(r.precip, spec.precip),
    fog: unit(r.fog, spec.fog),
    storm: unit(r.storm, spec.storm),
    isDay: typeof r.isDay === "boolean" ? r.isDay : null,
    at,
    // Anything read back from storage is by definition not a fresh fetch.
    stale: true,
  };
}

/**
 * Turn any thrown value into a line a user can act on.
 *
 * ctx.http rejects with a typed `kind`, which is why the proxy bothers to
 * distinguish 403/429/504/502 — "this host is not allowed" and "try again in a
 * minute" want very different words.
 * @param {unknown} e
 */
export function messageFor(e) {
  if (e instanceof WeatherDataError && e.message) return e.message;
  const kind = e && typeof e === "object" ? /** @type {any} */ (e).kind : undefined;
  if (kind === "denied") return "Weather blocked by OS policy";
  if (kind === "rate-limited") return "Too many requests — try shortly";
  if (kind === "timeout") return "Weather timed out";
  if (kind === "network") return "Offline";
  if (kind === "upstream") return "Weather service error";
  return "Weather unavailable";
}

/**
 * Normalize a location string. Empty/blank means "the default", never "".
 * @param {unknown} raw
 */
export function normalizeLocation(raw) {
  return typeof raw === "string" && raw.trim() ? raw.trim() : DEFAULT_LOCATION;
}
